import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, PiggyBank, ArrowLeft, RefreshCw, Plus, X,
  CheckCircle2, XCircle, ShieldCheck, AlertTriangle, Info,
  Clock, ChevronRight, DollarSign, Download, FileText, Mail, Send,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import XeroUploadSection from "@/components/XeroUploadSection";

/**
 * Feb 2026 — Change #2: Admin Payout Runs.
 *
 * Route: `/admin/partners/payouts`. Preview-only — no emails, no
 * Stripe. Groups a partner's approved (`payable`) ledger rows into
 * a single payout run, stamps each row with a
 * `PWS-PAYOUT-YYYY-MM-NNN` reference, and moves them to
 * `awaiting_payment` ("Payout scheduled").
 *
 * Layout: two tabs — Eligible partners | Payout runs. Detail modal
 * on click surfaces ledger rows + Mark paid / Cancel actions.
 */

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", minimumFractionDigits: 2,
});

const RUN_STATUS_STYLE = {
  awaiting_payment: { pill: "bg-amber-100 text-amber-800",     dot: "bg-amber-500",   label: "Awaiting payment" },
  paid:             { pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", label: "Paid" },
  cancelled:        { pill: "bg-slate-100 text-slate-500",     dot: "bg-slate-400",   label: "Cancelled" },
};

const BANK_STATUS_STYLE = {
  complete:   { pill: "bg-emerald-100 text-emerald-700", label: "Bank details complete" },
  incomplete: { pill: "bg-amber-100 text-amber-800",     label: "Bank details incomplete" },
  missing:    { pill: "bg-rose-100 text-rose-700",       label: "No bank details" },
};

function BankStatusChip({ status }) {
  const s = BANK_STATUS_STYLE[status] || BANK_STATUS_STYLE.missing;
  return (
    <span
      data-testid={`pws-payout-bank-status-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.pill}`}
    >
      {s.label}
    </span>
  );
}

function RunStatusPill({ status }) {
  const style = RUN_STATUS_STYLE[status] || RUN_STATUS_STYLE.cancelled;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${style.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {style.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU"); } catch { return "—"; }
}

// ─── Eligible partners preview (pre-create) ────────────────────────────
function EligibleRowsPreview({ partnerId, onCancel, onCreated }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/payout-runs/eligible/${encodeURIComponent(partnerId)}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerId]);

  const create = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/payout-runs", {
        partner_id: partnerId,
        notes:      notes || null,
      });
      toast.success(`Payout run ${data.payout_reference} created.`);
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="pws-payout-eligible-preview"
      className="rounded-2xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-600">Review</div>
          <h3 className="text-lg font-bold text-slate-900">Eligible ledger rows</h3>
          <p className="text-xs text-slate-500 mt-1">
            These are the approved commission rows that will be grouped into a new
            payout run. Rows will move from <span className="font-semibold">Approved</span> to <span className="font-semibold">Payout scheduled</span>.
            No money moves until you explicitly mark the run <span className="font-semibold">Paid</span>.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          data-testid="pws-payout-eligible-cancel-btn"
          className="text-slate-400 hover:text-slate-700"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {loading ? (
        <div data-testid="pws-payout-eligible-loading" className="p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : error ? (
        <div data-testid="pws-payout-eligible-error" className="p-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg">
          {error}
        </div>
      ) : !data?.rows?.length ? (
        <div data-testid="pws-payout-eligible-empty" className="p-8 text-center text-sm text-slate-500">
          No eligible ledger rows.
        </div>
      ) : (
        <>
          {/* ─── Bank-details warning (Feb 2026 P0 #1) ─── */}
          {data.bank_details_status && data.bank_details_status !== "complete" && (
            <div
              data-testid="pws-payout-bank-warning"
              className={`mb-3 rounded-xl p-3 text-xs flex items-start gap-2 ${
                data.bank_details_status === "missing"
                  ? "bg-rose-50 border border-rose-200 text-rose-900"
                  : "bg-amber-50 border border-amber-200 text-amber-900"
              }`}
            >
              <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
                data.bank_details_status === "missing" ? "text-rose-600" : "text-amber-600"
              }`} />
              <div>
                <span className="font-semibold">
                  {data.bank_details_status === "missing"
                    ? "This partner hasn&apos;t set up bank details yet."
                    : "This partner&apos;s bank details are incomplete."}
                </span>{" "}
                You can still create the payout run, but Aware admin will need the partner&apos;s bank
                details before the money can be transferred. Ask the partner to visit
                <span className="font-mono"> /partner/profile </span> and add their details.
              </div>
            </div>
          )}
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm" data-testid="pws-payout-eligible-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">Created</th>
                  <th className="text-left px-3 py-2 font-bold">Booking</th>
                  <th className="text-left px-3 py-2 font-bold">Source</th>
                  <th className="text-right px-3 py-2 font-bold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.ledger_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-xs text-slate-600 font-mono">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-2 text-xs font-mono font-semibold">{r.booking_reference}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.commission_source || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">
                      {AUD.format(r.commission_amount || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-xs text-slate-600 font-semibold text-right">
                    Total ({data.count} rows)
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">
                    {AUD.format(data.total_amount || 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mt-4">
            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Notes (optional)</label>
            <textarea
              data-testid="pws-payout-eligible-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Any internal notes for this run…"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={onCancel}
              data-testid="pws-payout-eligible-cancel-btn-2"
              className="rounded-full"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={create}
              disabled={submitting || !data?.rows?.length}
              data-testid="pws-payout-eligible-create-btn"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {submitting ? "Creating…" : `Create payout run — ${AUD.format(data.total_amount || 0)}`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Run detail drawer ─────────────────────────────────────────────────
function RunDetailDrawer({ reference, onClose, onMutated }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/payout-runs/${encodeURIComponent(reference)}`);
      setRun(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [reference]);
  useEffect(() => { reload(); }, [reload]);

  const markPaid = async () => {
    if (!window.confirm(`Mark payout ${reference} as PAID? This is a one-way action.`)) return;
    setBusy("paid");
    try {
      await api.post(`/admin/payout-runs/${encodeURIComponent(reference)}/mark-paid`, {});
      toast.success("Payout marked paid.");
      await reload();
      onMutated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const doCancel = async () => {
    setBusy("cancel");
    try {
      await api.post(`/admin/payout-runs/${encodeURIComponent(reference)}/cancel`, {
        reason: cancelReason || null,
      });
      toast.success("Payout cancelled. Ledger rows released.");
      setShowCancel(false);
      setCancelReason("");
      await reload();
      onMutated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const downloadPDF = async () => {
    setBusy("pdf");
    try {
      const resp = await api.get(
        `/admin/payout-runs/${encodeURIComponent(reference)}/pdf`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SCWT-Partner-Payout-Statement-${reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded.");
    } catch (e) {
      let detail = "";
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || ""; } catch { /* ignore */ }
      }
      toast.error(detail || formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  // Admin-only inline PDF preview. Uses the SAME endpoint as download
  // (authoriser + audit log intact). Because the endpoint expects a
  // Bearer header, we fetch as a blob then materialise a temporary
  // blob-URL for the <iframe>. No token ever appears in the URL.
  const openPreview = async () => {
    setPreviewLoading(true);
    try {
      const resp = await api.get(
        `/admin/payout-runs/${encodeURIComponent(reference)}/pdf`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (e) {
      let detail = "";
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || ""; } catch { /* ignore */ }
      }
      toast.error(detail || formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
  };

  const resendNotification = async (type, opts = {}) => {
    const label = type === "payout_paid" ? "Paid" : "Scheduled";
    const forceOverOptOut = !!opts.override_opt_out;
    if (forceOverOptOut) {
      const ok = window.confirm(
        `The partner has opted OUT of ${label.toLowerCase()} emails.\n\n` +
        `Force-send this email anyway? This is audit-logged.`,
      );
      if (!ok) return;
    }
    setBusy(`resend_${type}${forceOverOptOut ? "_force" : ""}`);
    try {
      const { data } = await api.post(
        `/admin/payout-runs/${encodeURIComponent(reference)}/notifications/resend`,
        { type, override_opt_out: forceOverOptOut },
      );
      if (data?.ok) {
        toast.success(
          `${label} email resent → ${data.effective_recipient}${
            data.override_applied ? " (preview override applied)" : ""
          }${
            data.force_over_opt_out ? " (over opt-out — audit logged)" : ""
          }.`,
        );
        await reload();
      } else if (data?.reason === "opted_out") {
        toast.warning(
          `${label} email NOT sent — partner has opted out.` +
          (type === "payout_paid" ? " Use 'Force resend' to override." : ""),
        );
      } else {
        toast.error(`${label} email not sent: ${data?.reason || data?.status || "unknown"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    // Clean up the blob-URL if the drawer unmounts with a preview open.
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white shadow-2xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="pws-payout-run-detail"
      >
        <header className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Payout run</div>
            <h2 className="text-lg font-bold text-slate-900 font-mono" data-testid="pws-payout-run-detail-reference">
              {reference}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pws-payout-run-detail-close-btn"
            className="text-slate-400 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-6">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
          ) : error ? (
            <div className="p-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg">{error}</div>
          ) : !run ? null : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Status</div>
                  <div className="mt-1"><RunStatusPill status={run.status} /></div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Total</div>
                  <div className="mt-1 text-lg font-mono font-bold text-slate-900" data-testid="pws-payout-run-detail-total">
                    {AUD.format(run.total_amount || 0)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Rows</div>
                  <div className="mt-1 text-sm font-semibold text-slate-800">{run.ledger_count}</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Created</div>
                  <div className="mt-1 text-xs text-slate-700">{fmtDateTime(run.created_at)}</div>
                  <div className="text-[10px] text-slate-400">{run.created_by}</div>
                </div>
                {run.paid_at && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 col-span-2">
                    <div className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Paid</div>
                    <div className="mt-1 text-xs text-emerald-800">{fmtDateTime(run.paid_at)} by {run.paid_by}</div>
                  </div>
                )}
                {run.cancelled_at && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 col-span-2">
                    <div className="text-[10px] font-semibold text-rose-700 uppercase tracking-wider">Cancelled</div>
                    <div className="mt-1 text-xs text-rose-800">{fmtDateTime(run.cancelled_at)} by {run.cancelled_by}</div>
                    {run.cancel_reason && <div className="mt-1 text-[11px] text-rose-700">{run.cancel_reason}</div>}
                  </div>
                )}
              </div>

              {run.notes && (
                <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                  {run.notes}
                </div>
              )}

              {/* PDF statement download — always available to admin */}
              <div
                data-testid="pws-payout-run-pdf-panel"
                className={`mb-4 rounded-lg border p-3 flex items-center justify-between gap-3 ${
                  run.status === "paid"
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-amber-50 border-amber-200"
                }`}
              >
                <div className="flex items-start gap-2 min-w-0">
                  <FileText className={`w-4 h-4 mt-0.5 shrink-0 ${
                    run.status === "paid" ? "text-emerald-600" : "text-amber-600"
                  }`} />
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${
                      run.status === "paid" ? "text-emerald-900" : "text-amber-900"
                    }`}>
                      Partner Monthly Payout Statement
                    </div>
                    <div className={`text-[11px] mt-0.5 ${
                      run.status === "paid" ? "text-emerald-700" : "text-amber-700"
                    }`}>
                      {run.status === "paid"
                        ? "Frozen at Mark-Paid — safe to share with the partner."
                        : "Preview only. PDF will be watermarked DRAFT — NOT PAID. Partner-facing download unlocks once you mark this run Paid."}
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={downloadPDF}
                  disabled={busy === "pdf"}
                  data-testid="pws-payout-run-pdf-btn"
                  className={`rounded-full shrink-0 ${
                    run.status === "paid"
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-amber-600 hover:bg-amber-700"
                  } text-white`}
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {busy === "pdf" ? "Preparing…" : (run.status === "paid" ? "Download PDF" : "Download draft PDF")}
                </Button>
              </div>
              <div className="mb-4 -mt-3 text-right">
                <button
                  type="button"
                  onClick={openPreview}
                  disabled={previewLoading || !!previewUrl}
                  data-testid="pws-payout-run-pdf-preview-btn"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 disabled:opacity-50"
                >
                  {previewLoading ? "Opening preview…" : "Preview PDF inline →"}
                </button>
              </div>

              {/* Notification status panel — Feb 2026 P1 */}
              <NotificationStatusPanel
                run={run}
                busy={busy}
                onResend={resendNotification}
              />

              <h3 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-600" /> Ledger rows in this run
              </h3>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm" data-testid="pws-payout-run-detail-table">                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold">Created</th>
                      <th className="text-left px-3 py-2 font-bold">Booking</th>
                      <th className="text-right px-3 py-2 font-bold">Amount</th>
                      <th className="text-left px-3 py-2 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(run.ledger_rows || []).map((r) => (
                      <tr key={r.ledger_id} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-xs text-slate-600 font-mono">{fmtDate(r.created_at)}</td>
                        <td className="px-3 py-2 text-xs font-mono font-semibold">{r.booking_reference}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">
                          {AUD.format(r.commission_amount || 0)}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.commission_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Phase X1 — Xero Upload panel (only enabled for paid runs) */}
              <XeroUploadSection
                reference={reference}
                runStatus={run.status}
                uploadState={run.xero_upload}
                onMutated={reload}
              />

              {run.status === "awaiting_payment" && (
                <div className="mt-6 flex flex-col gap-3">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={markPaid}
                      disabled={!!busy}
                      data-testid="pws-payout-run-mark-paid-btn"
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1.5" />
                      {busy === "paid" ? "Marking paid…" : "Mark paid"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowCancel(true)}
                      disabled={!!busy}
                      data-testid="pws-payout-run-open-cancel-btn"
                      className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      <XCircle className="w-4 h-4 mr-1.5" /> Cancel run
                    </Button>
                  </div>
                  {showCancel && (
                    <div
                      data-testid="pws-payout-run-cancel-panel"
                      className="rounded-xl border border-rose-200 bg-rose-50 p-3"
                    >
                      <div className="text-xs font-semibold text-rose-800 mb-2">
                        Cancelling releases the {run.ledger_count} ledger rows back to <span className="underline">Approved</span>. Provide a reason for the audit log.
                      </div>
                      <textarea
                        data-testid="pws-payout-run-cancel-reason"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={2}
                        maxLength={400}
                        placeholder="e.g. bank rejected, re-issuing next month, admin error"
                        className="w-full px-3 py-2 rounded-lg border border-rose-300 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none bg-white"
                      />
                      <div className="mt-2 flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setShowCancel(false); setCancelReason(""); }}
                          className="rounded-full"
                          disabled={busy === "cancel"}
                        >
                          Keep run
                        </Button>
                        <Button
                          size="sm"
                          onClick={doCancel}
                          disabled={busy === "cancel"}
                          data-testid="pws-payout-run-confirm-cancel-btn"
                          className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
                        >
                          {busy === "cancel" ? "Cancelling…" : "Confirm cancel"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {previewUrl && (
        <PdfPreviewModal
          reference={reference}
          status={run?.status}
          url={previewUrl}
          onClose={closePreview}
          onDownload={downloadPDF}
        />
      )}
    </div>
  );
}


// ─── Notification status panel (Feb 2026 P1) ───────────────────────────
function NotificationRow({ type, label, notif, disabled, onResend, disabledForce, onForceResend }) {
  const status = notif?.status;
  const isSent = status === "sent";
  const isFailed = status === "failed";
  const isOptedOut = status === "skipped_opted_out";
  const pillClass = isSent
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : isFailed
    ? "bg-rose-100 text-rose-800 border-rose-300"
    : isOptedOut
    ? "bg-amber-100 text-amber-900 border-amber-300"
    : status
    ? "bg-amber-100 text-amber-800 border-amber-300"
    : "bg-slate-100 text-slate-600 border-slate-300";
  const pillLabel = isSent
    ? "Sent"
    : isFailed
    ? "Failed"
    : isOptedOut
    ? "Skipped — partner opted out"
    : status === "skipped_no_recipient"
    ? "Skipped — no recipient"
    : status === "skipped_no_key"
    ? "Not sent — Resend key missing"
    : "Not sent";

  return (
    <div
      data-testid={`pws-payout-run-notif-row-${type}`}
      className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-slate-900">{label}</span>
          <span
            data-testid={`pws-payout-run-notif-status-${type}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${pillClass}`}
          >
            {pillLabel}
          </span>
          {notif?.override_applied && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-800 border border-indigo-300"
              title="Preview environment override applied"
            >
              preview override
            </span>
          )}
          {notif?.force_over_opt_out && (
            <span
              data-testid={`pws-payout-run-notif-force-badge-${type}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-900 border border-orange-300"
              title="Sent despite partner opt-out — audit logged"
            >
              over opt-out
            </span>
          )}
        </div>
        {notif ? (
          <div className="mt-1 text-[11px] text-slate-500 space-y-0.5">
            {notif.effective_recipient && (
              <div>
                <span className="font-semibold text-slate-600">To:</span>{" "}
                <span className="font-mono">{notif.effective_recipient}</span>
                {notif.override_applied && notif.intended_recipient && (
                  <span className="text-slate-400"> (intended → <span className="font-mono">{notif.intended_recipient}</span>)</span>
                )}
              </div>
            )}
            {notif.at && (
              <div>
                <span className="font-semibold text-slate-600">
                  {isSent ? "Sent:" : "At:"}
                </span>{" "}
                {new Date(notif.at).toLocaleString("en-AU")}
              </div>
            )}
            {notif.error && (
              <div className="text-rose-700">
                <span className="font-semibold">Error:</span> {notif.error}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-1 text-[11px] text-slate-400">
            No email has been triggered yet.
          </div>
        )}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        <button
          type="button"
          onClick={() => onResend(type)}
          disabled={disabled}
          data-testid={`pws-payout-run-notif-resend-btn-${type}`}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-indigo-700 hover:text-white hover:bg-indigo-600 border border-indigo-300 disabled:opacity-50 transition-colors"
        >
          <Send className="w-3 h-3" />
          {disabled ? "Sending…" : (notif ? "Resend" : "Send now")}
        </button>
        {/* Force resend is ONLY for paid emails (scheduled cannot be
            force-sent over opt-out per user constraint). Only visible
            when the partner is currently opted out. */}
        {isOptedOut && type === "payout_paid" && (
          <button
            type="button"
            onClick={() => onForceResend(type)}
            disabled={disabledForce}
            data-testid={`pws-payout-run-notif-force-btn-${type}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold text-orange-800 hover:text-white hover:bg-orange-600 border border-orange-300 disabled:opacity-50 transition-colors"
          >
            Force resend (over opt-out)
          </button>
        )}
      </div>
    </div>
  );
}


function NotificationStatusPanel({ run, busy, onResend }) {
  const notif = run.notified || {};
  const scheduled = notif.payout_scheduled;
  const paid      = notif.payout_paid;
  const showPaid  = run.status === "paid";
  const handleForce = (type) => onResend(type, { override_opt_out: true });

  return (
    <div
      data-testid="pws-payout-run-notif-panel"
      className="mb-4 rounded-lg border border-slate-200 bg-white p-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-indigo-600" />
        <h3 className="text-sm font-bold text-slate-900">Partner email notifications</h3>
      </div>
      <NotificationRow
        type="payout_scheduled"
        label="Payout scheduled email"
        notif={scheduled}
        disabled={busy === "resend_payout_scheduled"}
        disabledForce={false}
        onResend={onResend}
        onForceResend={handleForce}
      />
      {showPaid ? (
        <NotificationRow
          type="payout_paid"
          label="Payout paid email (with PDF)"
          notif={paid}
          disabled={busy === "resend_payout_paid"}
          disabledForce={busy === "resend_payout_paid_force"}
          onResend={onResend}
          onForceResend={handleForce}
        />
      ) : (
        <div
          data-testid="pws-payout-run-notif-paid-locked"
          className="pt-2 text-[11px] text-slate-400"
        >
          Payout paid email will fire once you mark this run <span className="font-semibold text-slate-600">Paid</span>.
        </div>
      )}
    </div>
  );
}


// ─── Admin-only inline PDF preview modal ───────────────────────────────
function PdfPreviewModal({ reference, status, url, onClose, onDownload }) {
  const isDraft = status !== "paid";

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-950/70 flex items-stretch justify-center p-2 sm:p-6"
      onClick={onClose}
      data-testid="pws-payout-run-pdf-preview-modal"
    >
      <div
        className="w-full max-w-5xl bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={`px-4 sm:px-6 py-3 flex items-center gap-3 border-b ${
          isDraft
            ? "bg-amber-50 border-amber-200"
            : "bg-emerald-50 border-emerald-200"
        }`}>
          <div className="min-w-0 flex-1">
            <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${
              isDraft ? "text-amber-700" : "text-emerald-700"
            }`}>
              {isDraft ? "Draft / Not paid — admin preview" : "Paid statement — admin preview"}
            </div>
            <div className="text-sm font-mono font-bold text-slate-900 truncate">
              {reference}
            </div>
          </div>
          {isDraft && (
            <span
              data-testid="pws-payout-run-pdf-preview-draft-badge"
              className="hidden sm:inline-flex items-center gap-1 rounded-full bg-amber-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
            >
              Draft / Not paid
            </span>
          )}
          <Button
            size="sm"
            onClick={onDownload}
            data-testid="pws-payout-run-pdf-preview-download-btn"
            className={`rounded-full text-white ${
              isDraft
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Download
          </Button>
          <button
            type="button"
            onClick={onClose}
            data-testid="pws-payout-run-pdf-preview-close-btn"
            className="text-slate-400 hover:text-slate-900 p-1"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {isDraft && (
          <div
            data-testid="pws-payout-run-pdf-preview-draft-banner"
            className="bg-amber-100 border-b border-amber-300 px-4 sm:px-6 py-2 text-[11px] text-amber-900"
          >
            <span className="font-bold">Draft / Not paid.</span>{" "}
            This preview is for admin review only — the partner-facing PDF is
            unavailable until this run is marked <span className="font-semibold">Paid</span>.
          </div>
        )}

        <div className="flex-1 bg-slate-100 min-h-[60vh]">
          <iframe
            src={url}
            title={`Preview of ${reference}`}
            className="w-full h-full"
            style={{ minHeight: "60vh", border: "none" }}
            data-testid="pws-payout-run-pdf-preview-iframe"
          />
        </div>
      </div>
    </div>
  );
}


// ─── Main page ─────────────────────────────────────────────────────────
export default function PWSPayoutRuns() {
  const [tab, setTab] = useState("eligible");
  const [eligible, setEligible] = useState(null);
  const [runs, setRuns] = useState(null);
  const [runsStatusFilter, setRunsStatusFilter] = useState("");
  const [loadingEligible, setLoadingEligible] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [detailRef, setDetailRef] = useState(null);

  const loadEligible = useCallback(async () => {
    setLoadingEligible(true);
    try {
      const { data } = await api.get("/admin/payout-runs/eligible");
      setEligible(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingEligible(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const params = {};
      if (runsStatusFilter) params.status = runsStatusFilter;
      const { data } = await api.get("/admin/payout-runs", { params });
      setRuns(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingRuns(false);
    }
  }, [runsStatusFilter]);

  useEffect(() => { loadEligible(); }, [loadEligible]);
  useEffect(() => { if (tab === "runs") loadRuns(); }, [tab, loadRuns]);

  const eligibleTotal = useMemo(() => {
    if (!eligible?.partners) return 0;
    return eligible.partners.reduce((s, p) => s + (p.eligible_amount || 0), 0);
  }, [eligible]);

  return (
    <PortalShell>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <Link
            to="/admin/partners/network"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Partner Network
          </Link>
        </div>

        <header className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-600">Admin</div>
          <div className="flex items-center gap-3 mt-1">
            <PiggyBank className="w-6 h-6 text-emerald-600" />
            <h1 className="text-3xl font-bold text-slate-900" data-testid="pws-payout-runs-page-title">Partner Payout Runs</h1>
          </div>
          <p className="text-sm text-slate-500 mt-2 max-w-3xl">
            Group approved partner commission rows into monthly payout runs.
            Creating a run stamps every included ledger row with a
            <span className="font-mono text-emerald-700"> PWS-PAYOUT-YYYY-MM-NNN </span>
            reference and moves it to <span className="font-semibold">Payout scheduled</span>.
            No money moves and no notifications are sent until you explicitly
            mark the run <span className="font-semibold">Paid</span>.
          </p>
        </header>

        {/* Safety banner */}
        <div
          data-testid="pws-payout-runs-safety-banner"
          className="mb-6 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 flex items-start gap-2"
        >
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
          <div>
            <span className="font-semibold">Preview only.</span>{" "}
            Payout runs are an admin-side ledger action. No emails, no bank
            transfers and no Stripe calls are triggered. Cancel a run any time
            before marking it paid — cancelling releases the ledger rows back to
            Approved.
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-6 flex gap-1">
          {[
            { id: "eligible", label: "Eligible partners", testid: "pws-payout-tab-eligible" },
            { id: "runs",     label: "Payout runs",       testid: "pws-payout-tab-runs" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.id
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Eligible partners */}
        {tab === "eligible" && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-slate-600">
                <span className="font-semibold">{eligible?.total_partners || 0}</span> partner(s) with approved rows to include —{" "}
                <span className="font-mono font-bold text-slate-900">{AUD.format(eligibleTotal)}</span> total.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadEligible}
                data-testid="pws-payout-eligible-refresh-btn"
                className="rounded-full"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
              </Button>
            </div>

            {selectedPartnerId && (
              <div className="mb-6">
                <EligibleRowsPreview
                  partnerId={selectedPartnerId}
                  onCancel={() => setSelectedPartnerId(null)}
                  onCreated={(run) => {
                    setSelectedPartnerId(null);
                    loadEligible();
                    setTab("runs");
                    setDetailRef(run.payout_reference);
                  }}
                />
              </div>
            )}

            {loadingEligible && !eligible ? (
              <div data-testid="pws-payout-eligible-list-loading" className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
                Loading eligible partners…
              </div>
            ) : (eligible?.partners || []).length === 0 ? (
              <div data-testid="pws-payout-eligible-list-empty" className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
                <div className="mb-2">All caught up.</div>
                <div className="text-xs text-slate-400">
                  No partners have approved commission rows awaiting a payout run.
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="pws-payout-eligible-list-table">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-bold">Partner</th>
                      <th className="text-left px-4 py-2.5 font-bold">Slug</th>
                      <th className="text-right px-4 py-2.5 font-bold">Rows</th>
                      <th className="text-right px-4 py-2.5 font-bold">Amount</th>
                      <th className="text-left px-4 py-2.5 font-bold">Oldest</th>
                      <th className="text-left px-4 py-2.5 font-bold">Bank</th>
                      <th className="text-right px-4 py-2.5 font-bold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligible.partners.map((p) => (
                      <tr
                        key={p.partner_id}
                        data-testid={`pws-payout-eligible-row-${p.partner_id}`}
                        className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors"
                      >
                        <td className="px-4 py-2.5 text-sm font-semibold text-slate-900">{p.partner_name}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{p.partner_slug || "—"}</td>
                        <td className="px-4 py-2.5 text-right text-sm font-semibold text-slate-800">{p.eligible_count}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900">{AUD.format(p.eligible_amount)}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{fmtDate(p.oldest_row_at)}</td>
                        <td className="px-4 py-2.5"><BankStatusChip status={p.bank_details_status || "missing"} /></td>
                        <td className="px-4 py-2.5 text-right">
                          <Button
                            size="sm"
                            onClick={() => setSelectedPartnerId(p.partner_id)}
                            data-testid={`pws-payout-eligible-preview-${p.partner_id}`}
                            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Review &amp; create
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Payout runs */}
        {tab === "runs" && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Status</label>
                <select
                  value={runsStatusFilter}
                  onChange={(e) => setRunsStatusFilter(e.target.value)}
                  data-testid="pws-payout-runs-status-filter"
                  className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs bg-white"
                >
                  <option value="">All</option>
                  <option value="awaiting_payment">Awaiting payment</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadRuns}
                data-testid="pws-payout-runs-refresh-btn"
                className="rounded-full"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
              </Button>
            </div>
            {loadingRuns && !runs ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">Loading…</div>
            ) : (runs?.rows || []).length === 0 ? (
              <div data-testid="pws-payout-runs-empty" className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
                No payout runs to show yet.
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="pws-payout-runs-table">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-bold">Reference</th>
                      <th className="text-left px-4 py-2.5 font-bold">Partner</th>
                      <th className="text-right px-4 py-2.5 font-bold">Rows</th>
                      <th className="text-right px-4 py-2.5 font-bold">Total</th>
                      <th className="text-left px-4 py-2.5 font-bold">Status</th>
                      <th className="text-left px-4 py-2.5 font-bold">Created</th>
                      <th className="text-right px-4 py-2.5 font-bold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.rows.map((r) => (
                      <tr
                        key={r.payout_run_id}
                        data-testid={`pws-payout-run-row-${r.payout_reference}`}
                        className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer transition-colors"
                        onClick={() => setDetailRef(r.payout_reference)}
                      >
                        <td className="px-4 py-2.5 font-mono text-xs font-semibold text-emerald-700">
                          {r.payout_reference}
                        </td>
                        <td className="px-4 py-2.5 text-xs font-mono text-slate-600">{r.partner_slug || r.partner_id}</td>
                        <td className="px-4 py-2.5 text-right text-sm font-semibold">{r.ledger_count}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900">{AUD.format(r.total_amount || 0)}</td>
                        <td className="px-4 py-2.5"><RunStatusPill status={r.status} /></td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <ChevronRight className="inline w-4 h-4 text-slate-400" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {detailRef && (
          <RunDetailDrawer
            reference={detailRef}
            onClose={() => setDetailRef(null)}
            onMutated={() => { loadEligible(); loadRuns(); }}
          />
        )}
      </div>
    </PortalShell>
  );
}
